/**
 * Phase 5a — marimba spawn sound.
 *
 * Single-event audio that fires whenever a new blob is spawned. A
 * `Tone.Sampler` is driven by 10 real marimba recordings from
 * Versilian Studios Chamber Orchestra 2: Community Edition (CC0, by
 * Samulis on Freesound — see /public/audio/marimba/_readme_and_
 * license.txt and the Sample credits section of SPEC.md / README.md).
 * The MetalSynth-based PoC was discarded after audition because real
 * marimba samples carry a wooden warmth and natural concert-hall
 * reverb that synthesis could not reproduce for the 金輪継ぎ
 * (kanawatsugi) installation.
 *
 *   Sampler → Reverb → Limiter → Destination
 *
 * Pitch is mapped from the blob's `objectSize` to a Japanese yo scale
 * spanning three octaves; the seed selects which of four yo tonics
 * (D / E / G / A) anchors the scale, so the same `?seed=…` URL
 * reproduces the same musical palette across reloads. The B tonic
 * was dropped because it would require ±4-semitone interpolation
 * for G#4 / G#6, beyond the threshold where marimba timbre stays
 * convincing — it can return in Phase 5b once additional samples
 * (e.g. G3 / A4 / G5) are added.
 *
 * Self-contained: nothing imports this module except `app.ts`. If the
 * PoC is replaced or deferred, a single `git revert` removes it
 * without touching the visual pipeline.
 */
import * as Tone from 'tone';

import { rngFromSeed } from '../utils/seed.js';

// ─── Yo-scale construction ──────────────────────────────────────────────────

/**
 * Four tonics that anchor coherent yo-scale variants. B is omitted
 * because the available samples force ±4-semitone interpolation for
 * G#4 / G#6 — see module header.
 */
const YO_TONICS = ['D', 'E', 'G', 'A'] as const;
type YoTonic = (typeof YO_TONICS)[number];

/** Yo intervals (in semitones from the tonic) — 2-3-2-2-3 pattern. */
const YO_INTERVALS_SEMI = [0, 2, 5, 7, 9];

/** Note range: three octaves starting at octave 3 → fifteen notes total. */
const OCTAVE_START = 3;
const OCTAVE_COUNT = 3;

/** Build a 15-note yo scale (3 octaves × 5 notes) anchored at `tonic`. */
function buildYoScale(tonic: YoTonic): string[] {
  const tonicMidi = Tone.Frequency(`${tonic}${OCTAVE_START}`).toMidi();
  const notes: string[] = [];
  for (let oct = 0; oct < OCTAVE_COUNT; oct++) {
    for (const semi of YO_INTERVALS_SEMI) {
      const midi = tonicMidi + oct * 12 + semi;
      notes.push(Tone.Frequency(midi, 'midi').toNote());
    }
  }
  return notes;
}

/** Pick the yo tonic deterministically from the world seed. */
function pickTonic(seed: string): YoTonic {
  // Suffix the seed so the audio RNG diverges from the colour /
  // physics RNGs — different seeds remain different, but identical
  // seeds don't accidentally couple visual and musical decisions.
  const rng = rngFromSeed(`${seed}:audio-tonic`);
  const idx = Math.floor(rng() * YO_TONICS.length);
  return YO_TONICS[idx] ?? 'D';
}

// ─── Pitch mapping ──────────────────────────────────────────────────────────

const OBJECT_SIZE_MIN = 0.15;
const OBJECT_SIZE_MAX = 0.45;
const NOTE_COUNT = OCTAVE_COUNT * YO_INTERVALS_SEMI.length;

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

/**
 * Map `objectSize` ∈ [0.15, 0.45] to a note index ∈ [0, 14].
 *
 * Larger blobs sound LOWER (index 0 = bottom of the scale), smaller
 * blobs sound HIGHER. This mirrors the spirit of Phase 2.5's
 * size-aware spawn duration: a bigger droplet has a slower, deeper
 * presence. Linear quantisation is sufficient at PoC scope; a
 * non-linear mapping can be revisited in Phase 5b.
 */
function sizeToNoteIndex(objectSize: number): number {
  const t = clamp01((OBJECT_SIZE_MAX - objectSize) / (OBJECT_SIZE_MAX - OBJECT_SIZE_MIN));
  return Math.round(t * (NOTE_COUNT - 1));
}

// ─── Public API ─────────────────────────────────────────────────────────────

/** Tunable signal-chain constants for the PoC. */
const SAMPLE_BASE_URL = '/audio/marimba/';
/**
 * Recorded sample set: 10 marimba notes spanning E2…C7 (~5 octaves).
 * Tone.Sampler picks the nearest semitone for each requested pitch
 * and shifts via playback rate, so any yo note plays back with at
 * most ±3 semitones of pitch shift across the four supported tonics.
 */
const SAMPLE_URLS: Record<string, string> = {
  E2: 'marimba-E2.wav',
  C3: 'marimba-C3.wav',
  F3: 'marimba-F3.wav',
  B3: 'marimba-B3.wav',
  E4: 'marimba-E4.wav',
  C5: 'marimba-C5.wav',
  F5: 'marimba-F5.wav',
  B5: 'marimba-B5.wav',
  E6: 'marimba-E6.wav',
  C7: 'marimba-C7.wav',
};
const REVERB_DECAY_SEC = 4.0;
const REVERB_WET = 0.55;
const REVERB_PREDELAY = 0.02;
const LIMITER_THRESHOLD_DB = -3;
const GLOBAL_VOLUME_DB = -6; // headroom for the fusion chord

// ─── Phase 5b — per-spawn variation + fusion chord ──────────────────────────

/** Golden-ratio fractional, used as a low-discrepancy sequence base. */
const PHI = 0.6180339887;

/**
 * Phase 5b — per-spawn timbre variation. The base note is anchored
 * by `objectSize` (Phase 5a behaviour, kept intact) but every
 * successive spawn shifts the note index by one of three "octave
 * bins" (= ±7 yo-scale-index steps, which is roughly an octave and a
 * fourth in semitones) and walks a velocity envelope decorrelated by
 * a doubled phase. Same `?seed=…` URL produces the same sequence
 * because the only input is the deterministic spawn counter.
 *
 * The bin selection uses golden-ratio low-discrepancy sampling
 * (`(n × φ) mod 1`) to avoid audible periodicity while staying
 * deterministic — over long sequences every bin is visited
 * uniformly with no obvious cycle length.
 */
function spawnVariation(
  spawnCount: number,
  baseNoteIdx: number,
  totalNotes: number,
): { noteIdx: number; velocity: number } {
  const pitchPhase = (spawnCount * PHI) % 1;
  // 3 octave bins, ~17-semitone spread up/down. Edges clamp into the
  // 15-note table so very-low or very-high baseNoteIdx still produces
  // valid notes (the variation just narrows by one bin at the rails).
  const octaveBins = [0, 7, -7];
  const octaveIdx = Math.floor(pitchPhase * octaveBins.length);
  const shifted = baseNoteIdx + (octaveBins[octaveIdx] ?? 0);
  const noteIdx = Math.max(0, Math.min(totalNotes - 1, shifted));

  const velPhase = (spawnCount * PHI * 2) % 1;
  const velocity = 0.75 + 0.25 * velPhase;

  return { noteIdx, velocity };
}

/**
 * Fusion-chord velocity. Slightly hotter than the per-spawn 0.75-1.0
 * range so the moment of contact reads as a punctuation rather than
 * "another spawn". Two simultaneous notes can momentarily exceed
 * unity gain at this velocity; the Limiter at -3 dBFS catches it.
 */
const FUSION_VELOCITY = 1.07;

export class SpawnSound {
  private readonly sampler: Tone.Sampler;
  private readonly reverb: Tone.Reverb;
  private readonly limiter: Tone.Limiter;
  /** Active 15-note scale (rebuilt by `reseed`). */
  private notes: string[];
  /**
   * Phase 5b — per-spawn variation counter. Increments on every
   * successful `play()`; reset to 0 on `reseed()` so the same
   * `?seed=…` URL produces the same melodic progression on reload.
   */
  private spawnCount = 0;
  /** Whether `Tone.start()` has resolved (= AudioContext is running). */
  private started = false;
  /** Whether `Tone.start()` failed once — keep the visuals running silent. */
  private disabled = false;
  /**
   * Phase 5c — MediaStream destination tap for WebM recording. Lazily
   * constructed on the first `getRecordStream()` so the AudioContext
   * is only burdened with a parallel sink when a recording starts.
   * Disconnected on `releaseRecordStream()` to keep speaker output
   * untouched between recording sessions.
   */
  private recordDestination: MediaStreamAudioDestinationNode | null = null;

  constructor(seed: string) {
    // Tone objects can be constructed before the AudioContext is
    // resumed; they only emit sound after `Tone.start()` succeeds.
    // Sample fetches kick off immediately on construction; the
    // sampler's `loaded` flag turns true once every URL has decoded.
    this.sampler = new Tone.Sampler({
      urls: SAMPLE_URLS,
      baseUrl: SAMPLE_BASE_URL,
      onerror: (err) => {
        console.warn('[audio] marimba sample failed to load:', err);
      },
    });

    this.reverb = new Tone.Reverb({
      decay: REVERB_DECAY_SEC,
      wet: REVERB_WET,
      preDelay: REVERB_PREDELAY,
    });

    this.limiter = new Tone.Limiter(LIMITER_THRESHOLD_DB);

    // sampler → reverb → limiter → destination
    this.sampler.chain(this.reverb, this.limiter, Tone.getDestination());

    Tone.getDestination().volume.value = GLOBAL_VOLUME_DB;

    this.notes = buildYoScale(pickTonic(seed));
  }

  /**
   * Re-seed the scale's tonic from a new world seed. Called from the
   * Reset path so the same `?seed=…` URL always reproduces the same
   * musical palette. Does not rebuild the sampler chain (cheap to
   * tear down only the note table).
   *
   * Phase 5b — also resets the spawn counter so the per-spawn
   * variation sequence starts from scratch with the new seed.
   */
  reseed(seed: string): void {
    this.notes = buildYoScale(pickTonic(seed));
    this.spawnCount = 0;
  }

  /**
   * Trigger one spawn sound for a blob of `objectSize` ∈ [0.15, 0.45].
   *
   * On the first call, lazily resumes the AudioContext via
   * `Tone.start()` — every caller MUST be invoked from a user
   * gesture (browser autoplay policy). Failures are absorbed and
   * the visual pipeline keeps running silent.
   *
   * If a click arrives before the sampler has finished loading its
   * 10 WAV files, the trigger is silently skipped — better to drop
   * the very first note than to block the visual spawn or play a
   * stuttering half-loaded sound.
   */
  async play(objectSize: number): Promise<void> {
    if (this.disabled) return;
    if (!this.started) {
      try {
        await Tone.start();
      } catch (err) {
        console.warn('[audio] Tone.start() failed; sound disabled:', err);
        this.disabled = true;
        return;
      }
      this.started = true;
    }
    if (!this.sampler.loaded) return; // samples still streaming in
    // Phase 5b — base note from objectSize, then apply per-spawn
    // octave-bin shift and velocity walk. Counter increments only
    // on successful triggers (skipped clicks during sample load
    // don't burn sequence positions).
    const baseIdx = sizeToNoteIndex(objectSize);
    const { noteIdx, velocity } = spawnVariation(
      this.spawnCount,
      baseIdx,
      this.notes.length,
    );
    const note = this.notes[noteIdx] ?? this.notes[0] ?? 'D4';
    // Marimba is percussive — let the recording's natural decay end
    // the note instead of imposing a release envelope.
    this.sampler.triggerAttack(note, undefined, velocity);
    this.spawnCount++;
  }

  /**
   * Phase 5b — fire a two-note chord on blob fusion. Caller passes
   * each blob's `objectSize` (0..1 fraction of the canvas short
   * edge, the same convention the spawn path uses); the chord is
   * composed of those two pitches at the current scale's natural
   * positions, with no per-spawn octave shift — fusion is its own
   * event, not "another spawn", so it stays anchored to the size
   * → pitch mapping.
   *
   * Both notes trigger on the same Tone.Sampler instance; Sampler
   * is implicitly polyphonic, so the chord rings out independently
   * over the sample's natural decay. The shared FUSION_VELOCITY is
   * slightly hotter than per-spawn velocities to mark the moment;
   * the Limiter catches any clipping from two simultaneous notes.
   */
  playFusion(sizeA: number, sizeB: number): void {
    if (this.disabled || !this.started || !this.sampler.loaded) return;
    const noteA = this.notes[sizeToNoteIndex(sizeA)] ?? this.notes[0] ?? 'D4';
    const noteB = this.notes[sizeToNoteIndex(sizeB)] ?? this.notes[0] ?? 'D4';
    this.sampler.triggerAttack([noteA, noteB], undefined, FUSION_VELOCITY);
  }

  /**
   * Phase 5c — return a `MediaStream` carrying Tone.js's master
   * output, suitable for mixing into `MediaRecorder`. Lazily wires
   * the limiter (last node before the master Destination) into a
   * fresh `MediaStreamAudioDestinationNode`; the existing speaker
   * path stays intact (Web Audio fan-out), so the audience still
   * hears the marimba while a recording captures it.
   *
   * Why tap the limiter, not `Tone.getDestination()`:
   *   `Tone.getDestination().connect(node)` walks `output` chains to
   *   the underlying native `GainNode` and *should* fan out, but in
   *   practice in Tone v15 we observed the resulting MediaStream
   *   producing a track with no audible signal (audio-track count
   *   was 1 but every recorded WebM came back silent). Tapping
   *   before the Destination wrapper sidesteps any internal mute /
   *   volume routing surprise, and the limiter is already the last
   *   gain-staged node in the chain (sampler → reverb → limiter →
   *   Destination), so the recording level is tracked.
   *
   * Safe to call before `Tone.start()` resolves: the underlying
   * `AudioContext` exists from construction time, just suspended.
   */
  getRecordStream(): MediaStream {
    if (this.recordDestination === null) {
      // Tone wraps the native AudioContext; `rawContext` exposes it
      // for direct Web Audio API operations like creating a stream
      // destination node.
      const ctx = Tone.getContext().rawContext as AudioContext;
      this.recordDestination = ctx.createMediaStreamDestination();
      // Fan-out from limiter: the existing limiter→Destination edge
      // (set up in the constructor's sampler.chain(...)) carries
      // audio to the speaker untouched, while this new edge carries
      // the same signal to the MediaStream destination.
      this.limiter.connect(this.recordDestination);
    }
    // Debug log requested for Wave 2 audio-record verification. Reports
    // the AudioContext state (must be 'running' for any real audio to
    // flow) and the resulting audio-track count on the MediaStream.
    // Remove with `[record-debug]` grep when verification is complete.
    console.log(
      '[record-debug] tone stream tracks:',
      this.recordDestination.stream.getAudioTracks().length,
      'context state:',
      Tone.getContext().state,
    );
    return this.recordDestination.stream;
  }

  /**
   * Phase 5c — tear down the recording tap. Called from the recording
   * stop path so the `MediaStreamAudioDestinationNode` and its tracks
   * don't linger between sessions. Idempotent.
   */
  releaseRecordStream(): void {
    if (this.recordDestination !== null) {
      try {
        // Mirror the connection point in `getRecordStream()`: the tap
        // is on the limiter, so the disconnect targets the limiter
        // too. Disconnecting the wrong node is a silent no-op in
        // Web Audio, which would leak the MediaStreamDestination.
        this.limiter.disconnect(this.recordDestination);
      } catch {
        // Already disconnected (e.g. dispose ran first) — not fatal.
      }
      this.recordDestination = null;
    }
  }

  /** Tear down the chain when the App stops. */
  dispose(): void {
    this.releaseRecordStream();
    this.sampler.dispose();
    this.reverb.dispose();
    this.limiter.dispose();
  }
}
